import { useState } from 'react';

import { Box, Button, Divider, Popover, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import DoneAllIcon from '@material-ui/icons/DoneAll';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import MoreHorizIcon from '@material-ui/icons/MoreHoriz';
import PopupState, { bindPopover, bindTrigger } from 'material-ui-popup-state';

import { ORDER_BY_OPTIONS, OrderByOption } from 'constants/orderBy';

import OrderByIndicator from './OrderByIndicator';
import { ReactComponent as OrderByIcon } from '../assets/images/orderby-icon.svg';

const useStyles = makeStyles(({ spacing, palette, typography }) => {
  const buttonSizes = {
    small: typography.pxToRem(40),
    medium: typography.pxToRem(48),
    large: typography.pxToRem(56),
  };

  return {
    selectContainer: {
      flexShrink: 0,
      marginRight: spacing(1),
    },
    selectButton: (props: { size: 'small' | 'medium' | 'large' }) => ({
      position: 'relative',
      width: '100%',
      height: buttonSizes[props.size] || buttonSizes.small,
      textTransform: 'none',
      justifyContent: 'space-between',
      paddingInline: spacing(1.5),
      border: `1px solid ${palette.divider}`,
      backgroundColor: 'rgba(0, 0, 0, 0.04)',
      '&:hover': {
        backgroundColor: 'rgba(0, 0, 0, 0.08)',
      },
    }),
    markAllAsReadButton: (props: { size: 'small' | 'medium' | 'large' }) => ({
      padding: 0,
      justifyContent: 'center',
      minWidth: buttonSizes[props.size] || buttonSizes.small,
      width: buttonSizes[props.size] || buttonSizes.small,
    }),
    selectButtonText: {
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      textAlign: 'left',
      flex: 1,
      marginRight: spacing(1),
    },
    popoverContent: {
      padding: spacing(1),
      width: 'max-content',
      maxWidth: 'calc(100vw - 32px)',
      display: 'flex',
      flexDirection: 'column',
      gap: spacing(0.5),
    },

    optionButton: {
      width: '100%',
      justifyContent: 'flex-start',
      textTransform: 'none',
      fontWeight: 400,
      padding: spacing(0.5, 1),
      borderRadius: spacing(0.5),
      textAlign: 'left',
      whiteSpace: 'nowrap',
    },

    submenuButtonContent: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      width: '100%',
      gap: spacing(1),
    },

    submenuButtonLabel: {
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    },
    orderByIcon: {
      height: typography.pxToRem(18),
      width: typography.pxToRem(18),

      '& > *': {
        fill: palette.action.active,
      },
    },
    divider: {
      marginLeft: typography.pxToRem(8),
      marginBlock: typography.pxToRem(6),
      width: typography.pxToRem(2),
      height: typography.pxToRem(20),
      color: 'inherit',
    },
  };
});

interface FilterListaAtendimentosProps {
  filteredOptions?: {
    orderBy?: OrderByOption;
    search?: string | undefined;
  };
  size?: 'small' | 'medium' | 'large';
  handleSetOrderBy?: (orderBy: OrderByOption) => void;
  onMarkAllAsRead?: () => void;
}

export default function FilterListaAtendimentos({
  filteredOptions,
  size = 'small',
  handleSetOrderBy,
  onMarkAllAsRead,
}: FilterListaAtendimentosProps) {
  const classes = useStyles({ size });
  const [subMenuAnchorEl, setSubMenuAnchorEl] =
    useState<HTMLButtonElement | null>(null);

  const currentOrderBy = filteredOptions?.orderBy;

  const isOrderingApplied =
    currentOrderBy && currentOrderBy !== ORDER_BY_OPTIONS.DEFAULT;

  const shouldShowOrderIndicator = Boolean(isOrderingApplied);

  const orderIndicatorProps = (() => {
    switch (currentOrderBy) {
      case ORDER_BY_OPTIONS.UNREAD:
        return {
          color: 'error' as const,
        };

      case ORDER_BY_OPTIONS.UNANSWERED:
        return {
          color: 'warning' as const,
        };

      default:
        return {};
    }
  })();

  const handleOrderByChange = (orderBy: OrderByOption) => {
    handleSetOrderBy?.(orderBy);
  };

  return (
    <Box className={classes.selectContainer}>
      <PopupState variant="popover" popupId="order-by-popover">
        {popupState => {
          const handleClosePopover = () => {
            popupState.close();
            setSubMenuAnchorEl(null);
          };

          return (
            <>
              {onMarkAllAsRead ? (
                <Button
                  {...bindTrigger(popupState)}
                  className={`${classes.selectButton} ${classes.markAllAsReadButton}`}
                  color="inherit"
                  size={size}
                >
                  {shouldShowOrderIndicator && (
                    <OrderByIndicator
                      isSelectIndicator
                      orderBy={currentOrderBy}
                      {...orderIndicatorProps}
                    />
                  )}

                  <MoreHorizIcon color="action" />
                </Button>
              ) : (
                <Button
                  {...bindTrigger(popupState)}
                  className={classes.selectButton}
                  endIcon={<ExpandMoreIcon color="action" />}
                  color="inherit"
                  size={size}
                >
                  {shouldShowOrderIndicator && (
                    <OrderByIndicator
                      isSelectIndicator
                      orderBy={currentOrderBy}
                      {...orderIndicatorProps}
                    />
                  )}

                  <OrderByIcon className={classes.orderByIcon} />

                  <Divider
                    orientation="vertical"
                    flexItem
                    className={classes.divider}
                  />
                </Button>
              )}

              <Popover
                {...bindPopover(popupState)}
                onClose={handleClosePopover}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                PaperProps={{ style: { marginTop: 8 } }}
              >
                <Box className={classes.popoverContent}>
                  {onMarkAllAsRead ? (
                    <>
                      {handleSetOrderBy && (
                        <Button
                          onClick={event => {
                            setSubMenuAnchorEl(event.currentTarget);
                          }}
                          className={classes.optionButton}
                          startIcon={
                            <OrderByIcon className={classes.orderByIcon} />
                          }
                        >
                          <Box className={classes.submenuButtonContent}>
                            <Typography
                              variant="body2"
                              className={classes.submenuButtonLabel}
                            >
                              Ordenar por
                            </Typography>

                            {shouldShowOrderIndicator && (
                              <OrderByIndicator
                                orderBy={currentOrderBy}
                                {...orderIndicatorProps}
                              />
                            )}
                          </Box>
                        </Button>
                      )}

                      <Button
                        onClick={() => {
                          handleClosePopover();
                          onMarkAllAsRead();
                        }}
                        className={classes.optionButton}
                        startIcon={<DoneAllIcon color="action" />}
                      >
                        Marcar todos como lidos
                      </Button>
                    </>
                  ) : (
                    <>
                      <Typography variant="body2" style={{ fontWeight: 500 }}>
                        Ordenar por:
                      </Typography>

                      <Button
                        startIcon={<OrderByIndicator />}
                        variant={!isOrderingApplied ? 'contained' : 'text'}
                        onClick={() => {
                          handleOrderByChange(ORDER_BY_OPTIONS.DEFAULT);
                          handleClosePopover();
                        }}
                        className={classes.optionButton}
                      >
                        Ordem de chegada
                      </Button>

                      <Button
                        startIcon={<OrderByIndicator color="warning" />}
                        variant={
                          currentOrderBy === ORDER_BY_OPTIONS.UNANSWERED
                            ? 'contained'
                            : 'text'
                        }
                        onClick={() => {
                          handleOrderByChange(ORDER_BY_OPTIONS.UNANSWERED);
                          handleClosePopover();
                        }}
                        className={classes.optionButton}
                      >
                        Última mensagem do contato
                      </Button>

                      <Button
                        startIcon={<OrderByIndicator color="error" />}
                        variant={
                          currentOrderBy === ORDER_BY_OPTIONS.UNREAD
                            ? 'contained'
                            : 'text'
                        }
                        onClick={() => {
                          handleOrderByChange(ORDER_BY_OPTIONS.UNREAD);
                          handleClosePopover();
                        }}
                        className={classes.optionButton}
                      >
                        Mensagens não lidas
                      </Button>
                    </>
                  )}
                </Box>
              </Popover>

              {onMarkAllAsRead && handleSetOrderBy && (
                <Popover
                  open={Boolean(subMenuAnchorEl)}
                  anchorEl={subMenuAnchorEl}
                  onClose={() => setSubMenuAnchorEl(null)}
                  anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                  transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                  PaperProps={{ style: { marginLeft: 8 } }}
                >
                  <Box className={classes.popoverContent}>
                    <Typography variant="body2" style={{ fontWeight: 500 }}>
                      Ordenar por:
                    </Typography>

                    <Button
                      startIcon={<OrderByIndicator />}
                      variant={!isOrderingApplied ? 'contained' : 'text'}
                      onClick={() => {
                        handleOrderByChange(ORDER_BY_OPTIONS.DEFAULT);
                        handleClosePopover();
                      }}
                      className={classes.optionButton}
                    >
                      Ordem de chegada
                    </Button>

                    <Button
                      startIcon={<OrderByIndicator color="warning" />}
                      variant={
                        currentOrderBy === ORDER_BY_OPTIONS.UNANSWERED
                          ? 'contained'
                          : 'text'
                      }
                      onClick={() => {
                        handleOrderByChange(ORDER_BY_OPTIONS.UNANSWERED);
                        handleClosePopover();
                      }}
                      className={classes.optionButton}
                    >
                      Última mensagem do contato
                    </Button>

                    <Button
                      startIcon={<OrderByIndicator color="error" />}
                      variant={
                        currentOrderBy === ORDER_BY_OPTIONS.UNREAD
                          ? 'contained'
                          : 'text'
                      }
                      onClick={() => {
                        handleOrderByChange(ORDER_BY_OPTIONS.UNREAD);
                        handleClosePopover();
                      }}
                      className={classes.optionButton}
                    >
                      Mensagens não lidas
                    </Button>
                  </Box>
                </Popover>
              )}
            </>
          );
        }}
      </PopupState>
    </Box>
  );
}
